import type { ErrorReporter, ErrorReport } from './error-reporter'

interface BugsnagEvent { severity: string; addMetadata: (section: string, metadata: Record<string, unknown>) => void }
interface BugsnagClient {
  start: (options: Record<string, unknown>) => void
  notify: (error: Error, cb?: (event: BugsnagEvent) => void) => void
  setUser: (id: string | null, email?: string, name?: string) => void
  addMetadata: (section: string, metadata: Record<string, unknown>) => void
  leaveBreadcrumb: (name: string, metadata?: Record<string, unknown>, type?: string) => void
  startSession: () => void
  pauseSession: () => void
  resumeSession: () => void
}

export interface BugsnagConfig {
  apiKey: string
  appVersion?: string
  releaseStage?: string
  enabledReleaseStages?: string[]
  user?: { id?: string; email?: string; name?: string }
  metadata?: Record<string, unknown>
  options?: {
    autoDetectErrors?: boolean
    autoTrackSessions?: boolean
    enabledBreadcrumbTypes?: string[]
    maxBreadcrumbs?: number
    onError?: (event: BugsnagEvent) => boolean | void
  }
}

export class BugsnagProvider implements ErrorReporter {
  private bugsnagClient: BugsnagClient | null = null
  private isInitialized = false
  private fallback: ErrorReporter
  constructor(private config: BugsnagConfig, fallback?: ErrorReporter) {
    this.fallback = fallback || {
      report: (error: Error | string, context?: Record<string, unknown>, severity?: string) => {
        // eslint-disable-next-line no-console
        console.error('[Bugsnag Fallback]', severity, error, context)
      }, setUser: () => {}, clearUser: () => {}
    }
    void this.initialize()
  }
  private async initialize(): Promise<void> {
    try {
      const Bugsnag = await this.loadBugsnagSdk()
      if (!Bugsnag) { console.warn('[Bugsnag] SDK not available, using fallback'); return }
      Bugsnag.start({
        apiKey: this.config.apiKey,
        appVersion: this.config.appVersion,
        releaseStage: this.config.releaseStage || 'development',
        enabledReleaseStages: this.config.enabledReleaseStages || ['staging', 'production'],
        autoDetectErrors: this.config.options?.autoDetectErrors ?? true,
        autoTrackSessions: this.config.options?.autoTrackSessions ?? true,
        enabledBreadcrumbTypes: this.config.options?.enabledBreadcrumbTypes || ['navigation','request','process','log','user','state','error'],
        maxBreadcrumbs: this.config.options?.maxBreadcrumbs || 40,
        onError: this.config.options?.onError,
      })
      this.bugsnagClient = Bugsnag
      this.isInitialized = true
      if (this.config.user) this.setUser(this.config.user.id || '', this.config.user.email, this.config.user.name)
      if (this.config.metadata) this.addMetadata('app', this.config.metadata)
    } catch (e) { console.error('[Bugsnag] Initialization failed:', e) }
  }
  private async loadBugsnagSdk(): Promise<BugsnagClient | null> {
    try {
      if (typeof window !== 'undefined' && (window as any).Bugsnag) return (window as any).Bugsnag as BugsnagClient
      try {
        // Avoid static resolution by bundlers to keep SDK optional
        const name = '@bugsnag/js'
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const sdk: any = await import(/* @vite-ignore */ (name as any)).catch(() => null)
        if (!sdk) { console.warn('[Bugsnag] SDK not installed. Run: npm install @bugsnag/js'); return null }
        return (sdk as any).default || (sdk as any)
      } catch { console.warn('[Bugsnag] SDK not installed. Run: npm install @bugsnag/js'); return null }
    } catch (e) { console.error('[Bugsnag] Failed to load SDK:', e); return null }
  }
  report(error: Error | string, context?: Record<string, unknown>, severity: ErrorReport['severity'] = 'error'): void {
    if (!this.isInitialized || !this.bugsnagClient) { this.fallback.report(error, context, severity); return }
    try {
      const errObj = typeof error === 'string' ? new Error(error) : error
      const sevMap: Record<ErrorReport['severity'], string> = { fatal: 'error', error: 'error', warning: 'warning', info: 'info' }
      this.bugsnagClient.notify(errObj, (event) => {
        event.severity = sevMap[severity]
        if (context) event.addMetadata('context', context)
        event.addMetadata('error', { reportedAt: new Date().toISOString(), severity })
      })
    } catch (e) { console.error('[Bugsnag] notify failed:', e); this.fallback.report(error, context, severity) }
  }
  setUser(userId: string, email?: string, name?: string): void {
    if (!this.isInitialized || !this.bugsnagClient) { this.fallback.setUser(userId); return }
    try { this.bugsnagClient.setUser(userId, email, name) } catch (e) { console.error('[Bugsnag] setUser failed:', e) }
  }
  clearUser(): void { if (!this.isInitialized || !this.bugsnagClient) { this.fallback.clearUser(); return } try { this.bugsnagClient.setUser(null) } catch (e) { console.error('[Bugsnag] clearUser failed:', e) } }
  leaveBreadcrumb(name: string, metadata?: Record<string, unknown>, type?: string): void { if (!this.isInitialized || !this.bugsnagClient) return; try { this.bugsnagClient.leaveBreadcrumb(name, metadata, type) } catch {} }
  addMetadata(section: string, metadata: Record<string, unknown>): void { if (!this.isInitialized || !this.bugsnagClient) return; try { this.bugsnagClient.addMetadata(section, metadata) } catch {} }
}
